import { sanitizeHtml } from '@/components/Functional/utils/sanitizeHtml'
import type { CSSProperties, FC } from 'react'
import styles from './HtmlContent.module.css'
import { VARIANT_STYLES } from './Typography'
import type { TypographyVariant } from './Typography'

type Props = {
  /** サニタイズ前の HTML 文字列（内部で sanitizeHtml を適用） */
  html: string
  /**
   * デフォルトテキストに適用する Typography variant（デフォルト: subtitle2）。
   * h1〜h5 要素は variant に関係なく見出しスタイルが適用される。
   */
  variant?: TypographyVariant
  /** 追加の className */
  className?: string
}

/**
 * CMS 由来の HTML 文字列を安全に描画し、Typography コンポーネントと同等のスタイルを適用する。
 *
 * - デフォルト文字（p / li / 素テキスト）: `variant` prop で指定した Typography variant
 * - h1〜h5 → 対応する Typography variant (28px〜16px / 700)
 * - strong / b → font-weight: 700
 * - ul / ol / li → リスト表示
 * - a → テキスト色継承 + 下線
 * - フォントファミリー: 'Noto Sans JP', sans-serif
 *
 * @example
 * <HtmlContent html={body} />
 * <HtmlContent html={body} variant="body1" className="mt-4" />
 */
export const HtmlContent: FC<Props> = ({ html, variant = 'subtitle2', className }) => {
  const vs = VARIANT_STYLES[variant]
  const variantStyle: CSSProperties = {
    fontSize: vs.fontSize,
    fontWeight: vs.fontWeight,
    lineHeight: vs.lineHeight,
    ...(vs.letterSpacing ? { letterSpacing: vs.letterSpacing } : {}),
  }

  return (
    <div
      className={[styles.container, className].filter(Boolean).join(' ')}
      style={variantStyle}
      // CMS 管理の HTML をサニタイズして表示する（noDangerouslySetInnerHtml は biome.json で off）
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  )
}
